export type Audience = "all" | "sisters" | "brothers" | "kids" | "youth";
export type RegistrationMode = "none" | "email" | "external";
export type RegistrationStatus = "registered" | "cancelled" | "waitlisted";
export type UserRole = "member" | "admin" | "board" | "volunteer";

export interface Profile {
    id: string;
    email: string | null;
    full_name: string | null;
    role: UserRole;
    created_at: string;
    updated_at: string;
    birth_date: string | null;
    phone_number: string | null;
    is_student: boolean;
    campus: string | null;
    other_campus: string | null;
    study_program: string | null;
    academic_year: string | null;
    subscribe_newsletter: boolean;
}

export interface EventSeries {
    id: string;
    slug: string;
    title: string;
    subtitle: string | null;
    description: string | null;
    intro_text: string | null;
    hero_image_url: string | null;
    accent_color: string | null;
    audience: Audience;
    starts_on: string | null;
    ends_on: string | null;
    is_published: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

export interface ImsalEvent {
    id: string;
    series_id: string | null;
    slug: string;
    title: string;
    subtitle: string | null;
    description: string | null;
    image_url: string | null;

    location: string | null;
    location_url: string | null;
    audience: Audience;
    category: string | null;
    price_text: string | null;
    age_range: string | null;
    speaker: string | null;

    starts_at: string | null;
    ends_at: string | null;
    all_day: boolean;
    time_note: string | null;
    is_tba: boolean;

    rrule: string | null;
    duration: string | null;
    exdates: string[] | null;

    registration_mode: RegistrationMode;
    google_form_url: string | null;
    registration_opens_at: string | null;
    registration_closes_at: string | null;
    capacity: number | null;

    is_published: boolean;
    /** Who created the event; receives registration notifications. */
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

/** An event joined with the series it belongs to (used by most views). */
export interface EventWithSeries extends ImsalEvent {
    series: Pick<
        EventSeries,
        "id" | "slug" | "title" | "accent_color" | "audience"
    > | null;
}

/** A series joined with its events (series detail page). */
export interface SeriesWithEvents extends EventSeries {
    events: ImsalEvent[];
}

export interface EventRegistration {
    id: string;
    event_id: string;
    occurrence_start: string | null;
    user_id: string | null;
    email: string;
    full_name: string | null;
    phone: string | null;
    notes: string | null;
    status: RegistrationStatus;
    manage_token: string;
    confirmation_sent_at: string | null;
    created_at: string;
}

/** Fields an admin may change on a registration. */
export type RegistrationPatch = Partial<
    Pick<EventRegistration, "full_name" | "email" | "phone" | "notes" | "status">
>;

export interface RegistrationWithEvent extends EventRegistration {
    event: Pick<
        ImsalEvent,
        "id" | "slug" | "title" | "starts_at" | "location" | "image_url"
    > | null;
}

/** Payload accepted by EventsService.register(). */
export interface RegistrationInput {
    eventId: string;
    occurrenceStart?: string | null;
    email: string;
    fullName?: string | null;
    phone?: string | null;
    notes?: string | null;
}

export const AUDIENCE_LABELS: Record<Audience, string> = {
    all: "Everyone welcome",
    sisters: "Sisters only",
    brothers: "Brothers only",
    kids: "Kids",
    youth: "Youth",
};

export const AUDIENCE_ICONS: Record<Audience, string> = {
    all: "groups",
    sisters: "diversity_1",
    brothers: "diversity_3",
    kids: "child_care",
    youth: "school",
};

export const DEFAULT_ACCENT = "#8acc17";
